import { Theme } from '@core/enums';
import animation, { fadeInLeft } from '@helpers/animation';
import brand from '@helpers/brand';
import { Device, from, until } from '@helpers/media';
import { slickCss, slickThemeCss } from '@helpers/slick';
import { SliderButtonStyles } from '@stories/Elements/Media/SliderButton/SliderButton.styles';
import styled, { css } from 'styled-components';

const Container = styled.div``;

const SliderWrapper = styled.div`
  ${slickCss};
  ${slickThemeCss}

  // below styles adjust slides to become equal height
  .slick-track {
    display: flex;
    align-items: stretch;
    justify-content: center;
  }

  .slick-slide {
    height: auto;

    * {
      user-select: none;

      @media ${from(Device.Tablet)} {
        -moz-user-drag: none;
        -webkit-user-drag: none;
      }
    }

    > div {
      height: 100%;
    }
  }
`;

const SliderControls = styled.div<{ inView: boolean }>`
  margin-top: 30px;
  opacity: 0;
  transform: translateX(-25px);
  ${({ inView }) => (inView ? fadeInLeft(inView, 25, 575, 425, animation.easeInOutCubic) : null)};

  @media ${from(Device.Tablet)} {
    position: absolute;
    top: 0;
    right: 24px;
    margin-top: unset;
  }

  @media ${from(Device.DesktopLarge)} {
    right: 30px;
  }

  ${SliderButtonStyles.SliderButtonElement} {
    margin-right: 10px;

    @media ${from(Device.Tablet)} {
      margin-left: 10px;
      margin-right: unset;
    }
  }
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
  margin-right: -39px;

  @media ${from(Device.Tablet)} {
    margin-right: unset;
    padding-right: 24px;
  }

  @media ${from(Device.DesktopLarge)} {
    padding-right: 30px;
  }

  ${SliderControls} {
    display: flex;
    position: unset;
    right: unset;
    margin-left: 20px;

    @media ${until(Device.Tablet)} {
      margin-top: 0;
    }

    ${SliderButtonStyles.SliderButtonElement} {
      margin-left: 10px;
      margin-right: unset;
    }
  }
`;

const SliderProgressBar = styled.div<{ themeOption: Theme }>`
  position: relative;
  height: 8px;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;

  ${({ themeOption }) => {
    switch (themeOption) {
      case Theme.White: {
        return css`
          ${() => SliderProgressBackground} {
            background-color: ${brand.grey[80]};
          }

          ${() => SliderProgressFill} {
            background-color: ${brand.red.base};
          }
        `;
      }

      case Theme.Red: {
        return css`
          ${() => SliderProgressBackground} {
            background-color: ${brand.grey[80]};
          }

          ${() => SliderProgressFill} {
            background-color: ${brand.red.base};
          }
        `;
      }

      case Theme.Black: {
        return css`
          ${() => SliderProgressBackground} {
            background-color: ${brand.white};
          }

          ${() => SliderProgressFill} {
            background-color: ${brand.black};
          }
        `;
      }

      default:
        break;
    }
  }};
`;

const SliderProgressBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: black;
`;

interface SliderProgressProps {
  percentage: number;
}

const SliderProgressFill = styled.div.attrs<SliderProgressProps>((props) => ({
  style: {
    transform: `scaleX(${props.percentage})`,
  },
}))<SliderProgressProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: scaleX(0);
  transform-origin: left;
  background-color: red;
  border-radius: 10px;
  transition: transform 400ms ease-out;
`;

export const CardSliderStyles = {
  Container,
  SliderWrapper,
  SliderControls,
  Bottom,
  SliderProgressBar,
  SliderProgressBackground,
  SliderProgressFill,
};
